import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { highlightMatch } from "../lib/highlightMatch";
import { DatePickerField } from "../components/DatePickerField";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  HardHat, Plus, ArrowLeft, Building2, CalendarClock, Euro, Users, FileText, Upload, Trash2,
  CheckCircle2, Circle, ListChecks, Layers, FolderOpen, X, Briefcase, UserCog, Landmark, Pencil, Paperclip, Megaphone, Send, Link2, FileDown, ThumbsUp, ThumbsDown, GripVertical, FileSignature, Sparkles, Clock,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, TouchSensor } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";


const STATO_CLS = {
  "Bozza": "bg-slate-100 text-slate-700 border-slate-300",
  "In corso": "bg-blue-100 text-blue-800 border-blue-300",
  "Sospeso": "bg-amber-100 text-amber-800 border-amber-300",
  "Concluso": "bg-green-100 text-green-800 border-green-300",
  "Annullato": "bg-red-100 text-red-800 border-red-300",
};
const FASE_CLS = {
  "Da avviare": "bg-slate-100 text-slate-600 border-slate-300",
  "In corso": "bg-blue-100 text-blue-800 border-blue-300",
  "Completata": "bg-green-100 text-green-800 border-green-300",
};
const eur = (n) => (Number(n || 0)).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");
const GANTT_FILL = { "Da avviare": "bg-slate-300", "In corso": "bg-blue-500", "Completata": "bg-green-500" };
const parseDay = (s) => (s ? new Date(String(s).slice(0, 10)).getTime() : null);

// Iter122n · Fase 1 · Sortable card wrapper per le fasi (dnd verticale).
// I listeners si applicano solo all'handle .drag-handle per evitare drag inavvertiti sui bottoni.
function SortableFaseCard({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : "auto" };
  return (
    <Card ref={setNodeRef} style={style} {...attributes} className={`p-3 border-2 ${isDragging ? "ring-2 ring-brand shadow-lg" : ""}`}
      data-testid={`fase-${id}`}>
      {typeof children === "function" ? children({ listeners: disabled ? {} : listeners }) : children}
    </Card>
  );
}

// Iter122k9 · Drop-zone drag&drop file per una fase. Accetta PDF + immagini (jpg/png/heic) + generici.
function FaseDropZone({ faseId, files = [], canEdit, onUpload, onDelete }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    if (!canEdit) return;
    const list = Array.from(e.dataTransfer?.files || []);
    if (list.length) onUpload(list);
  };
  const isImg = (name) => /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(name || "");
  const isPdf = (name) => /\.pdf$/i.test(name || "");
  return (
    <div className="mt-2">
      {canEdit && (
        <div
          data-testid={`fase-dropzone-${faseId}`}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`rounded-lg border-2 border-dashed px-3 py-2.5 text-xs cursor-pointer transition ${dragOver ? "border-brand bg-brand/5 text-brand" : "border-slate-300 text-slate-500 hover:border-brand/50 hover:bg-brand/[0.03]"}`}>
          <div className="inline-flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" />
            <span>Trascina qui PDF/immagini o <b className="underline">clicca per caricare</b></span>
          </div>
          <input ref={inputRef} type="file" hidden multiple
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
            data-testid={`fase-file-input-${faseId}`}
            onChange={(e) => { if (e.target.files?.length) { onUpload(Array.from(e.target.files)); e.target.value = ""; } }} />
        </div>
      )}
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`fase-files-${faseId}`}>
          {files.map((f) => {
            const label = f.file_name || f.filename || "documento";
            return (
              <div key={f.file_id} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full bg-slate-100 border border-slate-200 text-xs">
                <span className="text-slate-500">{isPdf(label) ? "📄" : isImg(label) ? "🖼️" : "📎"}</span>
                <button className="max-w-[180px] truncate text-brand hover:underline"
                  data-testid={`fase-file-open-${f.file_id}`}
                  onClick={() => openSecureFile(`/files/${f.file_id}`, label)}
                  title={label}>{label}</button>
                {canEdit && (
                  <button className="w-4 h-4 inline-flex items-center justify-center rounded-full text-slate-400 hover:bg-red-100 hover:text-red-600"
                    data-testid={`fase-file-del-${f.file_id}`}
                    onClick={(e) => { e.stopPropagation(); onDelete(f.file_id); }}
                    title="Elimina">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Iter122o · Fase 2 · pannello contabilità per una fase (fatture, note credito, acconti, saldi)
const PAG_LABEL = { fattura: "Fattura", nota_credito: "Nota di credito", acconto: "Acconto", saldo: "Saldo" };
const PAG_ICON_CLS = {
  fattura: "bg-indigo-100 text-indigo-800 border-indigo-300",
  nota_credito: "bg-orange-100 text-orange-800 border-orange-300",
  acconto: "bg-emerald-100 text-emerald-800 border-emerald-300",
  saldo: "bg-emerald-200 text-emerald-900 border-emerald-400",
};
function PagamentiFase({ fase, canEdit, onAdd, onEdit, onDelete, onUploadOne, onDeleteFile }) {
  const [open, setOpen] = useState(false);
  const pagamenti = fase.pagamenti || [];
  const previsto = Number(fase.importo || 0);
  const fatturato = Number(fase.totale_fatturato || 0);
  const pagato = Number(fase.totale_pagato || 0);
  const residuo = Math.max(fatturato - pagato, 0);
  return (
    <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`fase-contab-${fase.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs font-bold text-slate-600 hover:text-brand" data-testid={`fase-contab-toggle-${fase.id}`}>
          <Euro className="w-3.5 h-3.5" /> Contabilità · {pagamenti.length} mov.
          <span className="ml-1 text-slate-500 font-normal">
            {previsto ? `Previsto ${eur(previsto)} · ` : ""}
            Fatturato <b>{eur(fatturato)}</b> · Pagato <b>{eur(pagato)}</b>
            {residuo > 0 ? <span className="text-orange-700"> · Da pagare {eur(residuo)}</span> : null}
          </span>
        </button>
        {canEdit && (
          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`fase-contab-add-${fase.id}`} onClick={() => { setOpen(true); onAdd(); }}>
            <Plus className="w-3 h-3 mr-1" /> Nuovo movimento
          </Button>
        )}
      </div>
      {open && pagamenti.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {pagamenti.map((p) => (
            <div key={p.id} className="rounded border border-slate-200 bg-slate-50 p-2 text-xs" data-testid={`pag-${p.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded border font-bold uppercase tracking-wide ${PAG_ICON_CLS[p.tipo] || "bg-slate-100"}`}>{PAG_LABEL[p.tipo] || p.tipo}</span>
                {p.numero_documento && <span className="font-mono text-slate-700">{p.numero_documento}</span>}
                {p.data && <span className="text-slate-500">· {fmtDate(p.data)}</span>}
                <span className="font-bold text-slate-800 ml-auto">{eur(p.importo)}</span>
                {canEdit && (<>
                  <label className="cursor-pointer inline-flex items-center gap-0.5 text-slate-500 hover:text-brand" title="Carica allegati (fattura, ricevuta bonifico)">
                    <Upload className="w-3.5 h-3.5" />
                    <input type="file" multiple hidden onChange={(e) => { if (e.target.files?.length) { onUploadOne(p.id, Array.from(e.target.files)); e.target.value = ""; } }} data-testid={`pag-upload-${p.id}`} />
                  </label>
                  <button className="text-slate-500 hover:text-brand" onClick={() => onEdit(p)} data-testid={`pag-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button className="text-red-500 hover:text-red-700" onClick={() => onDelete(p.id)} data-testid={`pag-del-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </>)}
              </div>
              {p.note && <div className="text-slate-500 mt-0.5 italic">{p.note}</div>}
              {(p.files || []).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.files.map((f) => (
                    <div key={f.file_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border text-[11px]">
                      <button className="text-brand hover:underline" onClick={() => openSecureFile(`/files/${f.file_id}`, f.filename)} data-testid={`pag-file-${f.file_id}`}>
                        <Paperclip className="w-3 h-3 inline-block mr-0.5" />{(f.filename || "").slice(0, 30)}
                      </button>
                      {canEdit && <button className="text-red-400 hover:text-red-600" onClick={() => onDeleteFile(p.id, f.file_id)}><X className="w-3 h-3" /></button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Appalti() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStudio = user?.role === "admin";
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("panoramica");
  const [meta, setMeta] = useState({ ruoli: [], stati: [], categorie: [], catalogo: [], tipi_soggetto: [], stati_fase: [] });
  const [fornitori, setFornitori] = useState([]);
  const [condomini, setCondomini] = useState([]);
  const [residenti, setResidenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const uploadRef = useRef({});

  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ titolo: "", descrizione: "", condominio_ids: [], importo_previsto: "", data_avvio_presunta: "", stato: "Bozza" });
  const [sogOpen, setSogOpen] = useState(false);
  const [sog, setSog] = useState({ ruolo: "Direttore dei Lavori", tipo: "fornitore", ref_id: "", nome: "", email: "", telefono: "", note: "" });
  const [faseOpen, setFaseOpen] = useState(false);
  const [faseEditId, setFaseEditId] = useState(null);
  const [fase, setFase] = useState({ nome: "", tipo: "sal", data_inizio: "", data_fine: "", stato: "Da avviare", importo: "", note: "" });
  const [docOpen, setDocOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ categoria: "Contrattuale", tipologia: "", obbligatorio: false, soggetto_id: "" });

  const loadList = () => {
    setLoading(true);
    api.get("/appalti").then((r) => setList(r.data || [])).catch((e) => toast.error(formatApiError(e?.response?.data?.detail))).finally(() => setLoading(false));
  };
  const openDetail = (id) => {
    api.get(`/appalti/${id}`).then((r) => { setSel(r.data); setTab("panoramica"); }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  };
  const reload = () => { if (sel) openDetail(sel.id); loadList(); };

  useEffect(() => {
    loadList();
    api.get("/appalti/meta").then((r) => setMeta(r.data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/fornitori", { params: { limit: 5000 } }).then((r) => setFornitori(r.data.fornitori || [])).catch(() => {});
      api.get("/condomini").then((r) => setCondomini(Array.isArray(r.data) ? r.data : r.data.condomini || [])).catch(() => {});
      api.get("/residenti", { params: { limit: 5000 } }).then((r) => {
        const arr = Array.isArray(r.data) ? r.data : (r.data.residenti || r.data.utenti || []);
        setResidenti(arr.map((x) => ({ id: x.id, nome: x.name || x.nome || x.email, condominio_id: x.condominio_id, condominio: x.condominio_nome || "" })));
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user?.role]);

  const createAppalto = async () => {
    if (!form.titolo.trim()) return toast.error("Inserisci un titolo");
    try {
      const { data } = await api.post("/appalti", { ...form, importo_previsto: Number(form.importo_previsto || 0) });
      toast.success("Appalto creato");
      setNewOpen(false);
      setForm({ titolo: "", descrizione: "", condominio_ids: [], importo_previsto: "", data_avvio_presunta: "", stato: "Bozza" });
      loadList();
      setSel(data); setTab("panoramica");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const patchAppalto = async (patch) => {
    try { const { data } = await api.put(`/appalti/${sel.id}`, patch); setSel(data); loadList(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const delAppalto = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo appalto e i suoi documenti?")) return;
    try { await api.delete(`/appalti/${id}`); toast.success("Appalto eliminato"); setSel(null); loadList(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const addSoggetto = async () => {
    try { const { data } = await api.post(`/appalti/${sel.id}/soggetti`, sog); setSel(data); setSogOpen(false); setSog({ ruolo: "Direttore dei Lavori", tipo: "fornitore", ref_id: "", nome: "", email: "", telefono: "", note: "" }); loadList(); toast.success("Soggetto incaricato"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const delSoggetto = async (sid) => { try { const { data } = await api.delete(`/appalti/${sel.id}/soggetti/${sid}`); setSel(data); loadList(); } catch (e) { toast.error("Errore"); } };

  const openFaseNew = () => { setFaseEditId(null); setFase({ nome: "", tipo: "sal", data_inizio: "", data_fine: "", stato: "Da avviare", importo: "", note: "" }); setFaseOpen(true); };
  const openFaseEdit = (f) => { setFaseEditId(f.id); setFase({ nome: f.nome || "", tipo: f.tipo || "sal", data_inizio: (f.data_inizio || "").slice(0, 10), data_fine: (f.data_fine || "").slice(0, 10), stato: f.stato || "Da avviare", importo: f.importo || "", note: f.note || "" }); setFaseOpen(true); };
  const saveFase = async () => {
    if (!fase.nome.trim()) return toast.error("Inserisci il nome della fase");
    if (fase.data_inizio && fase.data_fine && fase.data_fine < fase.data_inizio) return toast.error("La data fine non può precedere l'inizio");
    try {
      const payload = { ...fase, importo: Number(fase.importo || 0) };
      const { data } = faseEditId ? await api.put(`/appalti/${sel.id}/fasi/${faseEditId}`, payload) : await api.post(`/appalti/${sel.id}/fasi`, payload);
      setSel(data); setFaseOpen(false); setFaseEditId(null); loadList();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const patchFase = async (f, patch) => { try { const { data } = await api.put(`/appalti/${sel.id}/fasi/${f.id}`, { ...f, ...patch, importo: Number((patch.importo ?? f.importo) || 0) }); setSel(data); loadList(); } catch (e) { toast.error("Errore"); } };
  const delFase = async (fid) => { try { const { data } = await api.delete(`/appalti/${sel.id}/fasi/${fid}`); setSel(data); loadList(); } catch (e) { toast.error("Errore"); } };
  // Iter122k9 · upload/delete file diretti sulla FASE (drag & drop)
  const uploadFaseFiles = async (faseId, files) => {
    if (!files || !files.length) return;
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await api.post(`/appalti/${sel.id}/fasi/${faseId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSel(data); loadList();
      toast.success(`${files.length} file caricato/i sulla fase.`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Upload fallito"); }
  };
  const delFaseFile = async (faseId, fileId) => {
    if (!window.confirm("Eliminare questo file?")) return;
    try { const { data } = await api.delete(`/appalti/${sel.id}/fasi/${faseId}/file/${fileId}`); setSel(data); loadList(); }
    catch { toast.error("Errore"); }
  };

  // Iter122o · Fase 2 · contabilità per fase (fatture / note credito / acconti / saldi + upload files)
  const [pagOpen, setPagOpen] = useState(false);
  const [pagFaseId, setPagFaseId] = useState(null);
  const [pagEditId, setPagEditId] = useState(null);
  const [pag, setPag] = useState({ tipo: "fattura", numero_documento: "", data: "", importo: "", note: "" });
  const openPagNew = (faseId) => { setPagFaseId(faseId); setPagEditId(null); setPag({ tipo: "fattura", numero_documento: "", data: "", importo: "", note: "" }); setPagOpen(true); };
  const openPagEdit = (faseId, p) => { setPagFaseId(faseId); setPagEditId(p.id); setPag({ tipo: p.tipo, numero_documento: p.numero_documento || "", data: (p.data || "").slice(0, 10), importo: p.importo ?? "", note: p.note || "" }); setPagOpen(true); };
  const savePag = async () => {
    if (!pag.tipo || !pag.importo) return toast.error("Compila tipo e importo");
    try {
      const body = { ...pag, importo: Number(pag.importo || 0) };
      const url = pagEditId ? `/appalti/${sel.id}/fasi/${pagFaseId}/pagamenti/${pagEditId}` : `/appalti/${sel.id}/fasi/${pagFaseId}/pagamenti`;
      const { data } = pagEditId ? await api.put(url, body) : await api.post(url, body);
      setSel(data); setPagOpen(false); loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const delPag = async (faseId, pid) => {
    if (!window.confirm("Eliminare questo movimento?")) return;
    try { const { data } = await api.delete(`/appalti/${sel.id}/fasi/${faseId}/pagamenti/${pid}`); setSel(data); loadList(); }
    catch (e) { toast.error("Errore"); }
  };
  const uploadPagFiles = async (faseId, pid, files) => {
    if (!files || !files.length) return;
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await api.post(`/appalti/${sel.id}/fasi/${faseId}/pagamenti/${pid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSel(data); toast.success("Allegato caricato");
    } catch (e) { toast.error("Upload fallito"); }
  };
  const delPagFile = async (faseId, pid, fileId) => {
    try { const { data } = await api.delete(`/appalti/${sel.id}/fasi/${faseId}/pagamenti/${pid}/file/${fileId}`); setSel(data); }
    catch (e) { toast.error("Errore"); }
  };

  // Iter122k27 · Attività su fase
  const [attFaseId, setAttFaseId] = useState("");
  const [attData, setAttData] = useState("");
  const [attTesto, setAttTesto] = useState("");
  const [attBusy, setAttBusy] = useState(false);
  const openAttForm = (fid) => {
    setAttFaseId(fid);
    setAttData(new Date().toISOString().slice(0, 10));
    setAttTesto("");
  };
  const addAttivita = async (fid) => {
    if (!attTesto.trim()) { toast.error("Inserisci una descrizione"); return; }
    setAttBusy(true);
    try {
      const { data } = await api.post(`/appalti/${sel.id}/fasi/${fid}/attivita`, { data: attData, testo: attTesto.trim() });
      setSel(data); setAttFaseId(""); setAttTesto("");
    } catch (e) { toast.error("Errore"); } finally { setAttBusy(false); }
  };
  const delAttivita = async (fid, atid) => {
    if (!window.confirm("Eliminare questa attività?")) return;
    try { const { data } = await api.delete(`/appalti/${sel.id}/fasi/${fid}/attivita/${atid}`); setSel(data); }
    catch (e) { toast.error("Errore"); }
  };
  // Iter122n · Fase 1 · drag & drop verticale delle fasi
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEndFasi = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const cur = sel?.fasi || [];
    const oldIndex = cur.findIndex((x) => x.id === active.id);
    const newIndex = cur.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(cur, oldIndex, newIndex).map((f, i) => ({ ...f, ordine: i }));
    // Optimistic update
    setSel({ ...sel, fasi: next });
    try {
      const { data } = await api.put(`/appalti/${sel.id}/fasi/riordina`, { fase_ids: next.map((f) => f.id) });
      setSel(data); loadList();
    } catch (e) {
      toast.error("Riordino non salvato");
      setSel({ ...sel, fasi: cur });
    }
  };

  const addDoc = async () => {
    if (!newDoc.tipologia.trim()) return toast.error("Inserisci la tipologia documento");
    try { const { data } = await api.post(`/appalti/${sel.id}/documenti`, newDoc); setSel(data); setDocOpen(false); setNewDoc({ categoria: "Contrattuale", tipologia: "", obbligatorio: false, soggetto_id: "" }); loadList(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const delDocSlot = async (docId) => { if (!window.confirm("Rimuovere questa voce documento?")) return; try { const { data } = await api.delete(`/appalti/documento/${docId}`); setSel(data); loadList(); } catch (e) { toast.error("Errore"); } };
  const uploadDoc = async (docId, fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    const fd = new FormData();
    arr.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/appalti/documento/${docId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSel(data); loadList(); toast.success(`${arr.length} file caricat${arr.length === 1 ? "o" : "i"}`);
    }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const removeFile = async (docId, fileId) => {
    try {
      const url = fileId ? `/appalti/documento/${docId}/file/${fileId}` : `/appalti/documento/${docId}/file`;
      const { data } = await api.delete(url); setSel(data); loadList();
    } catch (e) { toast.error("Errore"); }
  };

  const toggleCond = (id) => setForm((f) => ({ ...f, condominio_ids: f.condominio_ids.includes(id) ? f.condominio_ids.filter((x) => x !== id) : [...f.condominio_ids, id] }));

  // FASE 2: pratiche collegate
  const [pratOpen, setPratOpen] = useState(false);
  const [pratTipo, setPratTipo] = useState("manutenzione");
  const [pratSearch, setPratSearch] = useState("");
  const [pratList, setPratList] = useState([]);
  const loadPratiche = () => { if (!sel) return; api.get(`/appalti/${sel.id}/pratiche-disponibili`, { params: { tipo: pratTipo, search: pratSearch } }).then((r) => setPratList(r.data || [])).catch(() => setPratList([])); };
  useEffect(() => { if (pratOpen) { const tm = setTimeout(loadPratiche, 250); return () => clearTimeout(tm); } /* eslint-disable-next-line */ }, [pratOpen, pratTipo, pratSearch]);
  const linkPratica = async (refId) => { try { const { data } = await api.post(`/appalti/${sel.id}/pratiche`, { tipo: pratTipo, ref_id: refId }); setSel(data); toast.success("Pratica collegata (documenti importati)"); loadPratiche(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const unlinkPratica = async (lid) => { if (!window.confirm("Scollegare la pratica? I documenti importati verranno rimossi dall'appalto.")) return; try { const { data } = await api.delete(`/appalti/${sel.id}/pratiche/${lid}`); setSel(data); } catch (e) { toast.error("Errore"); } };

  // FASE 2: aggiornamenti / schede avanzamento
  const [aggOpen, setAggOpen] = useState(false);
  const [agg, setAgg] = useState({ titolo: "", testo: "", fase_id: "", soggetti_ids: [], doc_ids: [], includi_condomini: false });
  const toggleAggSog = (id) => setAgg((a) => ({ ...a, soggetti_ids: a.soggetti_ids.includes(id) ? a.soggetti_ids.filter((x) => x !== id) : [...a.soggetti_ids, id] }));
  const toggleAggDoc = (id) => setAgg((a) => ({ ...a, doc_ids: a.doc_ids.includes(id) ? a.doc_ids.filter((x) => x !== id) : [...a.doc_ids, id] }));
  const sendAgg = async () => {
    if (!agg.titolo.trim() || !agg.testo.trim()) return toast.error("Inserisci titolo e testo");
    if (agg.soggetti_ids.length === 0 && !agg.includi_condomini) return toast.error("Seleziona almeno un destinatario");
    try { const { data } = await api.post(`/appalti/${sel.id}/aggiornamenti`, agg); setSel(data); setAggOpen(false); setAgg({ titolo: "", testo: "", fase_id: "", soggetti_ids: [], doc_ids: [], includi_condomini: false }); toast.success("Scheda di avanzamento inviata"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const delAgg = async (aid) => { try { const { data } = await api.delete(`/appalti/${sel.id}/aggiornamenti/${aid}`); setSel(data); } catch (e) { toast.error("Errore"); } };

  // FASE 3: contabilità / extra capitolato
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraEditId, setExtraEditId] = useState(null);
  const [extra, setExtra] = useState({ descrizione: "", importo: "", motivo: "" });
  const openExtraNew = () => { setExtraEditId(null); setExtra({ descrizione: "", importo: "", motivo: "" }); setExtraOpen(true); };
  const openExtraEdit = (e) => { setExtraEditId(e.id); setExtra({ descrizione: e.descrizione || "", importo: e.importo ?? "", motivo: e.motivo || "" }); setExtraOpen(true); };
  const saveExtra = async () => {
    if (!extra.descrizione.trim()) return toast.error("Inserisci la descrizione");
    try {
      const body = { ...extra, importo: Number(extra.importo || 0) };
      if (extraEditId) {
        const { data } = await api.patch(`/appalti/${sel.id}/extra/${extraEditId}`, body);
        setSel(data);
      } else {
        const { data } = await api.post(`/appalti/${sel.id}/extra`, body);
        setSel(data);
      }
      setExtraOpen(false); setExtra({ descrizione: "", importo: "", motivo: "" }); setExtraEditId(null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const decidiExtra = async (eid, stato) => { try { const { data } = await api.put(`/appalti/${sel.id}/extra/${eid}`, { stato }); setSel(data); } catch (e) { toast.error("Errore"); } };
  const delExtra = async (eid) => { if (!window.confirm("Eliminare questa voce extra e tutti i movimenti/allegati collegati?")) return; try { const { data } = await api.delete(`/appalti/${sel.id}/extra/${eid}`); setSel(data); } catch (e) { toast.error("Errore"); } };
  // Iter122p · Fase 3 · upload allegati per extra
  const uploadExtraFiles = async (eid, files, tipo = "offerta") => {
    if (!files || !files.length) return;
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await api.post(`/appalti/${sel.id}/extra/${eid}/upload?tipo=${tipo}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSel(data); toast.success("Allegato caricato");
    } catch (e) { toast.error("Upload fallito"); }
  };
  const delExtraFile = async (eid, fileId) => {
    try { const { data } = await api.delete(`/appalti/${sel.id}/extra/${eid}/file/${fileId}`); setSel(data); }
    catch (e) { toast.error("Errore"); }
  };
  // Movimenti (fatture/acconti) su extra
  const [extraPagOpen, setExtraPagOpen] = useState(false);
  const [extraPagEid, setExtraPagEid] = useState(null);
  const [extraPag, setExtraPag] = useState({ tipo: "fattura", numero_documento: "", data: "", importo: "", note: "" });
  const openExtraPag = (eid) => { setExtraPagEid(eid); setExtraPag({ tipo: "fattura", numero_documento: "", data: "", importo: "", note: "" }); setExtraPagOpen(true); };
  const saveExtraPag = async () => {
    if (!extraPag.importo) return toast.error("Compila l'importo");
    try {
      const { data } = await api.post(`/appalti/${sel.id}/extra/${extraPagEid}/pagamenti`, { ...extraPag, importo: Number(extraPag.importo || 0) });
      setSel(data); setExtraPagOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const delExtraPag = async (eid, pid) => {
    if (!window.confirm("Eliminare questo movimento?")) return;
    try { const { data } = await api.delete(`/appalti/${sel.id}/extra/${eid}/pagamenti/${pid}`); setSel(data); }
    catch (e) { toast.error("Errore"); }
  };
  const downloadPdf = () => openSecureFile(`/appalti/${sel.id}/report/pdf`);

  // ───────── LIST VIEW ─────────
  if (!sel) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="appalti-page">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-brand flex items-center justify-center"><HardHat className="w-6 h-6 text-white" /></div>
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">Gestione Appalti</h1>
              <p className="text-sm text-slate-500">Direzione lavori, commesse, nomine, SAL e documentazione di cantiere.</p>
            </div>
          </div>
          {isStudio && <Button className="bg-brand hover:bg-[#63122F]" data-testid="appalto-new-btn" onClick={() => setNewOpen(true)}><Plus className="w-4 h-4 mr-1" /> Nuovo appalto</Button>}
        </div>

        {loading ? <p className="text-slate-500">Caricamento…</p> : list.length === 0 ? (
          <Card className="p-8 text-center text-slate-500 border-2 border-dashed">
            {isStudio ? "Nessun appalto. Crea il primo con «Nuovo appalto»." : "Non risultano appalti a te assegnati."}
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {list.map((a) => (
              <Card key={a.id} className="p-4 border-2 hover:border-[#C98AA0] cursor-pointer transition-colors" data-testid={`appalto-card-${a.id}`} onClick={() => openDetail(a.id)}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 text-lg">{a.titolo}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded border ${STATO_CLS[a.stato] || STATO_CLS.Bozza}`}>{a.stato}</span>
                    </div>
                    <div className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {a.condominio_nomi?.join(", ") || "—"}</span>
                      <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Avvio: {fmtDate(a.data_avvio_presunta)}</span>
                      <span className="flex items-center gap-1"><Euro className="w-3.5 h-3.5" /> {eur(a.importo_previsto)}</span>
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs text-slate-600 shrink-0">
                    <span className="flex items-center gap-1" title="Soggetti"><Users className="w-3.5 h-3.5" /> {a.soggetti_count}</span>
                    <span className="flex items-center gap-1" title="Fasi completate"><ListChecks className="w-3.5 h-3.5" /> {a.fasi_completate}/{a.fasi_totali}</span>
                    <span className={`flex items-center gap-1 ${a.obbligatori_mancanti ? "text-red-600 font-semibold" : ""}`} title="Documenti presenti"><FileText className="w-3.5 h-3.5" /> {a.documenti_presenti}/{a.documenti_totali}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        <NewAppaltoDialog {...{ newOpen, setNewOpen, form, setForm, condomini, meta, createAppalto, toggleCond }} />
      </div>
    );
  }

  // ───────── DETAIL VIEW ─────────
  const canEdit = sel.access_level === "studio";
  const docsByCat = {};
  (sel.documenti || []).forEach((d) => { (docsByCat[d.categoria] = docsByCat[d.categoria] || []).push(d); });
  const TABS = [["panoramica", "Panoramica", Layers], ["soggetti", "Soggetti & nomine", UserCog], ["timeline", "Timeline / SAL", ListChecks], ["contabilita", "Contabilità", Euro], ["documenti", "Documenti", FolderOpen], ["pratiche", "Pratiche collegate", Paperclip], ["aggiornamenti", "Avanzamento cantiere", Megaphone], ["copilot", "Sopralluoghi CoPilot", Sparkles]];

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="appalto-detail">
      <button className="text-sm text-brand font-semibold flex items-center gap-1 mb-3" data-testid="appalto-back" onClick={() => { setSel(null); loadList(); }}><ArrowLeft className="w-4 h-4" /> Tutti gli appalti</button>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-2xl font-bold text-slate-900">{sel.titolo}</h1>
            <span className={`text-xs font-bold px-2 py-0.5 rounded border ${STATO_CLS[sel.stato] || STATO_CLS.Bozza}`}>{sel.stato}</span>
            {sel.access_level === "ospite" && <span className="text-xs font-bold px-2 py-0.5 rounded border bg-[#FBEEF3] text-brand border-[#EBC5D2]">Incaricato</span>}
          </div>
          <p className="text-sm text-slate-500 mt-1">{sel.condominio_nomi?.join(", ")}</p>
        </div>
        {canEdit && <Button variant="outline" className="border-red-300 text-red-600 h-9" data-testid="appalto-delete" onClick={() => delAppalto(sel.id)}><Trash2 className="w-4 h-4 mr-1" /> Elimina</Button>}
      </div>

      <div className="flex gap-1 border-b-2 border-slate-200 mb-4 overflow-x-auto">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`appalto-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap border-b-2 -mb-0.5 ${tab === k ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "panoramica" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tab-panoramica">
          <Card className="p-4 border-2">
            <h3 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wide">Dati appalto</h3>
            <dl className="space-y-2 text-sm">
              <Row k="Importo previsto" v={eur(sel.importo_previsto)} />
              <Row k="Totale SAL" v={eur(sel.totale_sal)} />
              <Row k="Avvio presunto" v={fmtDate(sel.data_avvio_presunta)} />
              <Row k="Stabili" v={sel.condominio_nomi?.join(", ") || "—"} />
              <div className="flex gap-2 items-start"><span className="font-semibold text-slate-500 w-32 shrink-0">Stato</span>
                {canEdit ? (
                  <select className="border rounded px-2 py-1 text-sm" value={sel.stato} data-testid="appalto-stato-select" onChange={(e) => patchAppalto({ stato: e.target.value })}>
                    {meta.stati.map((s) => <option key={s}>{s}</option>)}
                  </select>
                ) : <span>{sel.stato}</span>}
              </div>
            </dl>
            {sel.descrizione && <p className="text-sm text-slate-600 mt-3 pt-3 border-t whitespace-pre-wrap">{sel.descrizione}</p>}
          </Card>
          <Card className="p-4 border-2">
            <h3 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wide">Avanzamento</h3>
            <ProgressBar label="Documenti presenti" value={sel.progress.documenti_presenti} total={sel.progress.documenti_totali} />
            <ProgressBar label="Fasi completate" value={sel.progress.fasi_completate} total={sel.progress.fasi_totali} />
            {sel.progress.obbligatori_mancanti > 0 && <p className="text-sm text-red-600 font-semibold mt-2">{sel.progress.obbligatori_mancanti} documenti obbligatori mancanti</p>}
            <div className="mt-3 pt-3 border-t text-sm text-slate-600 flex items-center gap-2"><Users className="w-4 h-4" /> {(sel.soggetti || []).length} soggetti coinvolti</div>
          </Card>
        </div>
      )}

      {tab === "soggetti" && (
        <div data-testid="tab-soggetti">
          {canEdit && <Button className="bg-brand hover:bg-[#63122F] mb-3" data-testid="soggetto-add-btn" onClick={() => setSogOpen(true)}><Plus className="w-4 h-4 mr-1" /> Incarica un soggetto</Button>}
          {(sel.soggetti || []).length === 0 ? <p className="text-slate-500 text-sm">Nessun soggetto incaricato.</p> : (
            <div className="grid grid-cols-1 gap-2">
              {sel.soggetti.map((s) => (
                <Card key={s.id} className="p-3 border-2 flex items-center justify-between gap-3" data-testid={`soggetto-${s.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#FBEEF3] flex items-center justify-center shrink-0">
                      {s.tipo === "fornitore" ? <Briefcase className="w-4 h-4 text-brand" /> : s.tipo === "condomino" ? <Landmark className="w-4 h-4 text-brand" /> : <Building2 className="w-4 h-4 text-brand" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{s.nome} <span className="text-xs font-normal text-slate-400">· {s.tipo === "committente-condominio" ? "committente" : s.tipo}</span></div>
                      <div className="text-sm text-brand font-medium">{s.ruolo}</div>
                      <div className="text-xs text-slate-500 truncate">{[s.email, s.telefono].filter(Boolean).join(" · ")}</div>
                    </div>
                  </div>
                  {canEdit && <Button size="sm" variant="ghost" className="text-red-500 shrink-0" data-testid={`soggetto-del-${s.id}`} onClick={() => delSoggetto(s.id)}><Trash2 className="w-4 h-4" /></Button>}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "timeline" && (() => {
        const fasi = sel.fasi || [];
        const rows = fasi.map((f) => {
          const s = parseDay(f.data_inizio) ?? parseDay(f.data_fine);
          const e = parseDay(f.data_fine) ?? parseDay(f.data_inizio);
          return { f, s, e: e != null && s != null && e < s ? s : e };
        });
        const withDates = rows.filter((r) => r.s != null);
        const min = withDates.length ? Math.min(...withDates.map((r) => r.s)) : 0;
        const max = withDates.length ? Math.max(...withDates.map((r) => r.e)) : 0;
        const span = Math.max(max - min, 86400000);
        const months = [];
        if (withDates.length) {
          const d = new Date(min); d.setDate(1);
          const end = new Date(max);
          while (d.getTime() <= end.getTime()) {
            months.push({ t: d.getTime(), label: d.toLocaleDateString("it-IT", { month: "short", year: "2-digit" }), pct: ((d.getTime() - min) / span) * 100 });
            d.setMonth(d.getMonth() + 1);
          }
        }
        const gridPcts = months.map((m) => m.pct).filter((p) => p > 0.5 && p < 99.5);
        return (
          <div data-testid="tab-timeline">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-sm text-slate-500">Diagramma di Gantt: durata pianificata (inizio → fine) di ogni fase. Totale SAL: <b>{eur(sel.totale_sal)}</b></p>
              {canEdit && <Button className="bg-brand hover:bg-[#63122F]" data-testid="fase-add-btn" onClick={openFaseNew}><Plus className="w-4 h-4 mr-1" /> Aggiungi fase</Button>}
            </div>
            {withDates.length > 0 && (
              <div className="relative h-5 mb-1 border-b border-slate-200" data-testid="gantt-axis">
                {months.map((m, i) => (
                  <span key={i} className="absolute top-0 text-[10px] text-slate-400 whitespace-nowrap" style={{ left: `${Math.max(m.pct, 0)}%` }}>{m.label}</span>
                ))}
                <span className="absolute top-0 right-0 text-[10px] text-slate-500 font-medium">{fmtDate(new Date(max).toISOString())}</span>
              </div>
            )}
            {rows.length === 0 && <p className="text-sm text-slate-400 py-4">Nessuna fase inserita.</p>}
            {/* Iter122n · Fase 1 · drag & drop verticale per riordinare le fasi (solo admin) */}
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEndFasi}>
              <SortableContext items={rows.map(({ f }) => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {rows.map(({ f, s, e }) => {
                    const has = s != null;
                    const left = has ? ((s - min) / span) * 100 : 0;
                    const width = has ? Math.max(((e - s) / span) * 100, 1.5) : 0;
                    return (
                      <SortableFaseCard key={f.id} id={f.id} disabled={!canEdit}>
                        {({ listeners }) => (<>
                        <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            {canEdit && (
                              <span data-testid={`fase-drag-handle-${f.id}`} {...listeners}
                                className="mt-0.5 -ml-1 text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing touch-none select-none"
                                title="Trascina per riordinare">
                                <GripVertical className="w-5 h-5" />
                              </span>
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-800">{f.nome} <span className="text-xs text-slate-400">· {f.tipo}</span></div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {has ? `${fmtDate(f.data_inizio || f.data_fine)} → ${fmtDate(f.data_fine || f.data_inizio)}` : "Date non pianificate"}
                                {f.importo ? ` · ${eur(f.importo)}` : ""}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {canEdit ? (
                              <select className={`text-xs font-bold px-2 py-1 rounded border ${FASE_CLS[f.stato]}`} value={f.stato} data-testid={`fase-stato-${f.id}`} onChange={(ev) => patchFase(f, { stato: ev.target.value })}>
                                {meta.stati_fase.map((st) => <option key={st}>{st}</option>)}
                              </select>
                            ) : <span className={`text-xs font-bold px-2 py-1 rounded border ${FASE_CLS[f.stato]}`}>{f.stato}</span>}
                            {canEdit && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500" data-testid={`fase-edit-${f.id}`} onClick={() => openFaseEdit(f)}><Pencil className="w-4 h-4" /></Button>}
                            {canEdit && <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" data-testid={`fase-del-${f.id}`} onClick={() => delFase(f.id)}><Trash2 className="w-4 h-4" /></Button>}
                          </div>
                        </div>
                        <div className="relative h-6 rounded bg-slate-100 overflow-hidden" data-testid={`gantt-track-${f.id}`}>
                          {gridPcts.map((p, i) => (
                            <span key={i} className="absolute top-0 h-full w-px bg-slate-200" style={{ left: `${p}%` }} />
                          ))}
                          {has ? (
                            <div className={`absolute top-0 h-full rounded ${GANTT_FILL[f.stato] || "bg-slate-300"} opacity-90`} style={{ left: `${left}%`, width: `${width}%`, minWidth: "6px" }} data-testid={`gantt-bar-${f.id}`} />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-400">— nessuna data —</div>
                          )}
                        </div>
                        {f.note && <p className="text-xs text-slate-500 mt-1">{f.note}</p>}
                        {/* Iter122k9 · Drag & drop file (PDF/immagini) sulla fase */}
                        <FaseDropZone faseId={f.id} files={f.files || []} canEdit={canEdit}
                          onUpload={(list) => uploadFaseFiles(f.id, list)}
                          onDelete={(fid_file) => delFaseFile(f.id, fid_file)} />
                        {/* Iter122o · Fase 2 · Contabilità di fase */}
                        <PagamentiFase fase={f} canEdit={canEdit}
                          onAdd={() => openPagNew(f.id)}
                          onEdit={(p) => openPagEdit(f.id, p)}
                          onDelete={(pid) => delPag(f.id, pid)}
                          onUpload={(files) => uploadPagFiles(f.id, undefined, files) /* placeholder for group upload if needed */}
                          onUploadOne={(pid, files) => uploadPagFiles(f.id, pid, files)}
                          onDeleteFile={(pid, fid_file) => delPagFile(f.id, pid, fid_file)}
                        />
                        {/* Iter122k27 · Attività di fase: log di eventi con data e testo */}
                        <div className="mt-3 border-t pt-3" data-testid={`fase-att-block-${f.id}`}>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1.5">
                              <ListChecks className="w-3.5 h-3.5" /> Attività di fase
                              {(f.attivita || []).length > 0 && (
                                <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px]">{f.attivita.length}</span>
                              )}
                            </div>
                            {canEdit && attFaseId !== f.id && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`fase-att-add-btn-${f.id}`}
                                onClick={() => openAttForm(f.id)}>
                                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi attività
                              </Button>
                            )}
                          </div>
                          {attFaseId === f.id && (
                            <div className="rounded-lg border-2 border-dashed border-brand/30 bg-brand/5 p-2 mb-2 space-y-2" data-testid={`fase-att-form-${f.id}`}>
                              <div className="flex gap-2 items-end">
                                <div>
                                  <label className="text-[10px] font-bold uppercase text-slate-500">Data</label>
                                  <Input type="date" data-testid={`fase-att-data-${f.id}`}
                                    value={attData} onChange={(e) => setAttData(e.target.value)}
                                    className="h-9 w-40" />
                                </div>
                                <div className="flex-1">
                                  <label className="text-[10px] font-bold uppercase text-slate-500">Descrizione</label>
                                  <Input data-testid={`fase-att-testo-${f.id}`} value={attTesto}
                                    onChange={(e) => setAttTesto(e.target.value)}
                                    placeholder="Es. Sopralluogo con DL, ordine materiali..." className="h-9" />
                                </div>
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button size="sm" variant="outline" className="h-8" onClick={() => { setAttFaseId(""); setAttTesto(""); }}>Annulla</Button>
                                <Button size="sm" data-testid={`fase-att-save-${f.id}`} className="h-8 bg-brand hover:bg-[#63122F]"
                                  disabled={attBusy || !attTesto.trim()} onClick={() => addAttivita(f.id)}>
                                  <Plus className="w-3.5 h-3.5 mr-1" /> Salva
                                </Button>
                              </div>
                            </div>
                          )}
                          {(f.attivita || []).length === 0 ? (
                            <div className="text-xs text-slate-400 italic">Nessuna attività registrata.</div>
                          ) : (
                            <ul className="space-y-1" data-testid={`fase-att-list-${f.id}`}>
                              {[...(f.attivita || [])].sort((a, b) => (a.data || "").localeCompare(b.data || "")).map((it) => (
                                <li key={it.id} className="flex items-start justify-between gap-2 text-xs bg-slate-50 rounded px-2 py-1.5" data-testid={`fase-att-item-${it.id}`}>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-mono font-bold text-slate-700">{fmtDate(it.data) || "—"}</span>
                                    <span className="text-slate-800 ml-2">{it.testo}</span>
                                    {it.autore_nome && (
                                      <span className="text-[10px] text-slate-500 ml-2">· {it.autore_nome}</span>
                                    )}
                                  </div>
                                  {canEdit && (
                                    <button type="button" onClick={() => delAttivita(f.id, it.id)}
                                      className="text-red-500 hover:text-red-700 shrink-0"
                                      data-testid={`fase-att-del-${it.id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        </>)}
                      </SortableFaseCard>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        );
      })()}

      {tab === "documenti" && (
        <div data-testid="tab-documenti">
          {canEdit && <Button className="bg-brand hover:bg-[#63122F] mb-3" data-testid="doc-add-btn" onClick={() => setDocOpen(true)}><Plus className="w-4 h-4 mr-1" /> Aggiungi documento</Button>}
          {sel.access_level === "ospite" && <p className="text-sm text-slate-500 mb-3">Puoi caricare e aggiornare la documentazione delle tue pratiche man mano che l'appalto avanza.</p>}
          {(meta.categorie || []).filter((c) => docsByCat[c]?.length).map((cat) => (
            <div key={cat} className="mb-4">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-600 mb-2 border-b pb-1">{cat}</h3>
              <div className="grid grid-cols-1 gap-2">
                {docsByCat[cat].map((d) => (
                  <div key={d.id} className="flex items-start gap-2 p-2 rounded border-2 border-slate-100 hover:border-slate-200" data-testid={`doc-${d.id}`}>
                    {d.presente ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-1" /> : <Circle className={`w-5 h-5 shrink-0 mt-1 ${d.obbligatorio ? "text-red-400" : "text-slate-300"}`} />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2 flex-wrap">
                        <span className="break-words">{d.tipologia}</span>
                        {d.obbligatorio && <span className="text-[10px] font-bold text-red-500 border border-red-200 rounded px-1">OBBL.</span>}
                        {(d.files || []).length > 1 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`doc-count-${d.id}`}>
                            {d.files.length} allegati
                          </span>
                        )}
                      </div>
                      {(d.files || []).length > 0 && (
                        <div className="mt-1 space-y-1" data-testid={`doc-files-${d.id}`}>
                          {d.files.map((f) => (
                            <div key={f.file_id} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`doc-file-${f.file_id}`}>
                              <FileText className="w-3.5 h-3.5 text-brand shrink-0" />
                              <button type="button" className="text-slate-700 font-medium hover:underline truncate min-w-0 flex-1 text-left"
                                onClick={() => openSecureFile(`/files/${f.file_id}`)}
                                title="Apri" data-testid={`doc-file-view-${f.file_id}`}>{f.file_name}</button>
                              <button type="button" className="text-amber-700 hover:text-amber-800 shrink-0"
                                title="Rimuovi questo file" onClick={() => removeFile(d.id, f.file_id)} data-testid={`doc-file-del-${f.file_id}`}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <input type="file" className="hidden" multiple ref={(el) => (uploadRef.current[d.id] = el)} onChange={(e) => { uploadDoc(d.id, e.target.files); e.target.value = ""; }} />
                      <Button size="sm" variant="outline" className="h-8" title="Aggiungi file (anche più di uno)" data-testid={`doc-upload-${d.id}`} onClick={() => uploadRef.current[d.id]?.click()}><Upload className="w-4 h-4" /></Button>
                      {canEdit && <Button size="sm" variant="ghost" className="h-8 text-red-500" title="Elimina voce" data-testid={`doc-del-${d.id}`} onClick={() => delDocSlot(d.id)}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "contabilita" && (() => {
        const c = sel.contabilita || {};
        const chart = [
          { n: "Previsto", v: c.importo_previsto || 0, f: "#94a3b8" },
          { n: "Autorizzato", v: c.totale_autorizzato || 0, f: "#7A193D" },
          { n: "SAL completati", v: c.sal_completati || 0, f: "#16a34a" },
          { n: "Extra appr.", v: c.extra_approvati || 0, f: "#2563eb" },
        ];
        const EX_CLS = { Proposto: "bg-amber-100 text-amber-800 border-amber-300", Approvato: "bg-green-100 text-green-800 border-green-300", Respinto: "bg-red-100 text-red-800 border-red-300" };
        return (
          <div data-testid="tab-contabilita">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-sm text-slate-500">Quadro economico: importo previsto, SAL, extra capitolato autorizzati e da liquidare.</p>
              <Button variant="outline" className="border-brand/40 text-brand" data-testid="report-pdf-btn" onClick={downloadPdf}><FileDown className="w-4 h-4 mr-1" /> Report PDF</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="p-4 border-2">
                <h3 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wide">Quadro economico</h3>
                <dl className="space-y-2 text-sm">
                  <Row k="Importo previsto" v={eur(c.importo_previsto)} />
                  <Row k="Extra approvati" v={eur(c.extra_approvati)} />
                  <Row k="Totale autorizzato" v={<b className="text-brand">{eur(c.totale_autorizzato)}</b>} />
                  <Row k="SAL completati" v={eur(c.sal_completati)} />
                  <Row k="Da liquidare" v={eur(c.da_liquidare)} />
                  <Row k="Extra in attesa" v={eur(c.extra_proposti)} />
                  {/* Iter122o · Fase 2 · contabilità aggregata dai movimenti per fase */}
                  <Row k="Totale fatturato" v={<b className="text-indigo-800">{eur(c.totale_fatturato)}</b>} />
                  <Row k="Totale pagato" v={<b className="text-emerald-700">{eur(c.totale_pagato)}</b>} />
                  <Row k="Residuo da pagare" v={<b className="text-orange-700">{eur(c.residuo_da_pagare)}</b>} />
                </dl>
              </Card>
              <Card className="p-4 border-2">
                <h3 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wide">Grafico costi</h3>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart data={chart} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                      <XAxis dataKey="n" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 10 }} width={48} />
                      <Tooltip formatter={(v) => eur(v)} />
                      <Bar dataKey="v" radius={[4, 4, 0, 0]}>{chart.map((e, i) => <Cell key={i} fill={e.f} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
            {/* Iter122o · Fase 2 · Riepilogo contabilità per fase */}
            <div className="mt-5">
              <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide mb-2">Contabilità per fase (Timeline)</h3>
              {(sel.fasi || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic">Nessuna fase inserita. Aggiungi le fasi dalla scheda Timeline / SAL per iniziare a caricare fatture e pagamenti.</p>
              ) : (
                <div className="rounded border-2 border-slate-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold">
                      <tr>
                        <th className="text-left px-3 py-2">Fase</th>
                        <th className="text-right px-3 py-2">Previsto</th>
                        <th className="text-right px-3 py-2">Fatturato</th>
                        <th className="text-right px-3 py-2">Pagato</th>
                        <th className="text-right px-3 py-2">Residuo</th>
                        <th className="text-center px-3 py-2">Mov.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sel.fasi || []).map((f) => {
                        const prev = Number(f.importo || 0);
                        const fatt = Number(f.totale_fatturato || 0);
                        const pag = Number(f.totale_pagato || 0);
                        const res = Math.max(fatt - pag, 0);
                        return (
                          <tr key={f.id} className="border-t border-slate-100" data-testid={`contab-fase-row-${f.id}`}>
                            <td className="px-3 py-2"><b className="text-slate-800">{f.nome}</b> <span className="text-[10px] text-slate-400 uppercase">{f.tipo}</span></td>
                            <td className="px-3 py-2 text-right">{eur(prev)}</td>
                            <td className="px-3 py-2 text-right text-indigo-800">{eur(fatt)}</td>
                            <td className="px-3 py-2 text-right text-emerald-700">{eur(pag)}</td>
                            <td className={`px-3 py-2 text-right ${res > 0 ? "text-orange-700 font-bold" : "text-slate-400"}`}>{eur(res)}</td>
                            <td className="px-3 py-2 text-center text-slate-500">{(f.pagamenti || []).length}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-5 mb-2 flex-wrap gap-2">
              <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Costi extra capitolato</h3>
              {canEdit && <Button className="bg-brand hover:bg-[#63122F]" size="sm" data-testid="extra-add-btn" onClick={openExtraNew}><Plus className="w-4 h-4 mr-1" /> Nuovo costo extra</Button>}            </div>
            {(sel.extra_costi || []).length === 0 ? <p className="text-slate-500 text-sm">Nessun costo extra.</p> : (
              <div className="grid grid-cols-1 gap-2">
                {sel.extra_costi.map((e) => {
                  const efatt = Number(e.totale_fatturato || 0);
                  const epag = Number(e.totale_pagato || 0);
                  const eres = Math.max(efatt - epag, 0);
                  return (
                  <Card key={e.id} className="p-3 border-2" data-testid={`extra-${e.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-800">{e.descrizione} <span className="text-brand">{eur(e.importo)}</span></div>
                        <div className="text-xs text-slate-500">{e.deciso_da ? `${e.stato} da ${e.deciso_da}` : `Proposto da ${e.creato_da}`}{e.motivo ? ` · ${e.motivo}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded border ${EX_CLS[e.stato]}`}>{e.stato}</span>
                        {canEdit && e.stato === "Proposto" && (<>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-green-600" title="Approva" data-testid={`extra-approva-${e.id}`} onClick={() => decidiExtra(e.id, "Approvato")}><ThumbsUp className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" title="Respingi" data-testid={`extra-respingi-${e.id}`} onClick={() => decidiExtra(e.id, "Respinto")}><ThumbsDown className="w-4 h-4" /></Button>
                        </>)}
                        {canEdit && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-indigo-700" title="Modifica" data-testid={`extra-edit-${e.id}`} onClick={() => openExtraEdit(e)}><Pencil className="w-4 h-4" /></Button>}
                        {canEdit && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400" title="Elimina" data-testid={`extra-del-${e.id}`} onClick={() => delExtra(e.id)}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </div>
                    {/* Iter122p · Fase 3 · allegati offerta extra + Iter122t · editor sottoscrizione */}
                    <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Allegati offerta</div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {canEdit && (
                            <label className="cursor-pointer text-xs inline-flex items-center gap-1 px-2 py-1 rounded border-2 border-slate-300 hover:border-brand text-slate-600 hover:text-brand" data-testid={`extra-upload-${e.id}`}>
                              <Upload className="w-3.5 h-3.5" /> Carica PDF offerta
                              <input type="file" hidden multiple accept="application/pdf,image/*" onChange={(ev) => { if (ev.target.files?.length) { uploadExtraFiles(e.id, Array.from(ev.target.files), "offerta"); ev.target.value = ""; } }} />
                            </label>
                          )}
                          {canEdit && (() => {
                            const pdfOfferta = (e.files || []).find((f) => f.tipo === "offerta" && ((f.file_name || f.filename || "").toLowerCase().endsWith(".pdf")));
                            const disabled = !pdfOfferta;
                            return (
                              <Button size="sm" className="h-7 text-xs bg-brand hover:bg-[#63122F]" data-testid={`extra-editor-${e.id}`}
                                disabled={disabled}
                                title={disabled ? "Carica prima una PDF offerta extra capitolato" : "Apri editor per timbrare e firmare l'offerta"}
                                onClick={() => {
                                  const q = new URLSearchParams();
                                  q.set("contesto", "appalto_extra");
                                  q.set("appalto_id", sel.id);
                                  q.set("extra_id", e.id);
                                  q.set("source_file_id", pdfOfferta.file_id);
                                  if (sel.condominio_id) q.set("condominio_id", sel.condominio_id);
                                  navigate(`/conferma-preventivo?${q.toString()}`);
                                }}>
                                <FileSignature className="w-3.5 h-3.5 mr-1" /> Editor sottoscrizione
                              </Button>
                            );
                          })()}
                        </div>
                      </div>
                      {(e.files || []).length === 0 ? (
                        <p className="text-[11px] text-slate-400 italic">Nessun allegato. Carica il PDF dell&apos;offerta extra capitolato: potrai poi aprire l&apos;editor per timbrare, apporre logo/firma e sottoscriverla (stessa esperienza di Preventivi Ginius).</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {e.files.map((f) => (
                            <div key={f.file_id} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border-2 text-[11px]">
                              <button className="text-brand hover:underline" onClick={() => openSecureFile(`/files/${f.file_id}`, f.filename)}>
                                <Paperclip className="w-3 h-3 inline-block mr-0.5" />{(f.filename || "").slice(0, 40)}
                                {f.tipo && <span className="text-slate-400 ml-1">· {f.tipo}</span>}
                              </button>
                              {canEdit && <button className="text-red-400 hover:text-red-600" onClick={() => delExtraFile(e.id, f.file_id)}><X className="w-3 h-3" /></button>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Iter122p · Fase 3 · contabilità extra (fatture/acconti) */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1">
                          <Euro className="w-3.5 h-3.5" /> Contabilità extra · {(e.pagamenti || []).length} mov.
                          <span className="ml-1 text-slate-500 font-normal">
                            Fatturato <b>{eur(efatt)}</b> · Pagato <b>{eur(epag)}</b>
                            {eres > 0 && <span className="text-orange-700"> · Da pagare {eur(eres)}</span>}
                          </span>
                        </div>
                        {canEdit && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`extra-pag-add-${e.id}`} onClick={() => openExtraPag(e.id)}><Plus className="w-3 h-3 mr-1" /> Movimento</Button>}
                      </div>
                      {(e.pagamenti || []).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {e.pagamenti.map((p) => (
                            <div key={p.id} className="flex items-center gap-2 flex-wrap text-xs rounded border border-slate-200 bg-slate-50 p-2" data-testid={`extra-pag-${p.id}`}>
                              <span className={`px-1.5 py-0.5 rounded border font-bold uppercase text-[10px] ${PAG_ICON_CLS[p.tipo]}`}>{PAG_LABEL[p.tipo]}</span>
                              {p.numero_documento && <span className="font-mono">{p.numero_documento}</span>}
                              {p.data && <span className="text-slate-500">· {fmtDate(p.data)}</span>}
                              <span className="font-bold ml-auto">{eur(p.importo)}</span>
                              {canEdit && <button className="text-red-500" onClick={() => delExtraPag(e.id, p.id)}><Trash2 className="w-3.5 h-3.5" /></button>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {tab === "pratiche" && (
        <div data-testid="tab-pratiche">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-sm text-slate-500">Collega preventivi e manutenzioni già sottoscritti: contratti, allegati e foto vengono importati automaticamente nei Documenti.</p>
            {canEdit && <Button className="bg-brand hover:bg-[#63122F]" data-testid="pratica-link-btn" onClick={() => { setPratOpen(true); setPratSearch(""); }}><Link2 className="w-4 h-4 mr-1" /> Collega pratica</Button>}
          </div>
          {(sel.pratiche_collegate || []).length === 0 ? <p className="text-slate-500 text-sm">Nessuna pratica collegata.</p> : (
            <div className="grid grid-cols-1 gap-2">
              {sel.pratiche_collegate.map((p) => (
                <Card key={p.id} className="p-3 border-2 flex items-center justify-between gap-3" data-testid={`pratica-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate"><span className="text-xs font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 mr-2">{p.tipo === "manutenzione" ? "Manutenzione" : "Preventivo"}</span>{p.numero} · {p.titolo}</div>
                    <div className="text-xs text-slate-500 truncate">{[p.fornitore_nome, p.stato, p.importo ? eur(p.importo) : ""].filter(Boolean).join(" · ")}</div>
                  </div>
                  {canEdit && <Button size="sm" variant="ghost" className="text-red-500 shrink-0" data-testid={`pratica-unlink-${p.id}`} onClick={() => unlinkPratica(p.id)}><Trash2 className="w-4 h-4" /></Button>}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "aggiornamenti" && (
        <div data-testid="tab-aggiornamenti">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-sm text-slate-500">Schede di avanzamento cantiere inviate ai soggetti coinvolti e/o ai condòmini (email + notifica app).</p>
            {canEdit && <Button className="bg-brand hover:bg-[#63122F]" data-testid="agg-new-btn" onClick={() => setAggOpen(true)}><Send className="w-4 h-4 mr-1" /> Nuova scheda avanzamento</Button>}
          </div>
          {(sel.aggiornamenti || []).length === 0 ? <p className="text-slate-500 text-sm">Nessuna scheda inviata.</p> : (
            <div className="grid grid-cols-1 gap-2">
              {sel.aggiornamenti.map((a) => (
                <Card key={a.id} className="p-3 border-2" data-testid={`agg-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800">{a.titolo}{a.fase_nome ? <span className="text-xs text-slate-400 font-normal"> · {a.fase_nome}</span> : ""}</div>
                      <div className="text-xs text-slate-400">{fmtDate(a.created_at)} · {a.creato_da}</div>
                    </div>
                    {canEdit && <Button size="sm" variant="ghost" className="text-red-500 shrink-0 h-7 w-7 p-0" onClick={() => delAgg(a.id)}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{a.testo}</p>
                  {(a.destinatari || []).length > 0 && <div className="text-xs text-slate-500 mt-2 pt-2 border-t">Inviata a: {a.destinatari.map((d) => d.nome).join(", ")}</div>}
                  <div className="mt-2">
                    <Button size="sm" variant="outline" className="h-8 border-brand/40 text-brand" data-testid={`agg-pdf-${a.id}`} onClick={() => openSecureFile(`/appalti/${sel.id}/aggiornamenti/${a.id}/pdf`)}><FileDown className="w-4 h-4 mr-1" /> Scarica scheda PDF</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Iter123k199 · Tab Sopralluoghi CoPilot */}
      {tab === "copilot" && (
        <CoPilotTab sel={sel} canEdit={canEdit} />
      )}

      {/* Dialog: collega pratica */}
      <Dialog open={pratOpen} onOpenChange={setPratOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Collega una pratica</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {["manutenzione", "preventivo"].map((tp) => (
                <button key={tp} data-testid={`prat-tipo-${tp}`} onClick={() => setPratTipo(tp)} className={`px-3 py-1.5 rounded text-sm font-semibold border ${pratTipo === tp ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-300"}`}>{tp === "manutenzione" ? "Manutenzioni" : "Preventivi"}</button>
              ))}
            </div>
            <Input data-testid="prat-search" placeholder="Cerca per numero, titolo, fornitore…" value={pratSearch} onChange={(e) => setPratSearch(e.target.value)} />
            <div className="border rounded max-h-72 overflow-y-auto divide-y">
              {pratList.length === 0 ? <p className="p-3 text-sm text-slate-400">Nessuna pratica disponibile.</p> : pratList.map((p) => (
                <button key={p.id} data-testid={`prat-opt-${p.id}`} onClick={() => linkPratica(p.id)} className="w-full text-left px-3 py-2 hover:bg-slate-50">
                  <div className="text-sm font-semibold text-slate-800 truncate">{highlightMatch(p.numero, pratSearch)} · {highlightMatch(p.titolo, pratSearch)}</div>
                  <div className="text-xs text-slate-500 truncate">{highlightMatch([p.fornitore_nome, p.stato].filter(Boolean).join(" · "), pratSearch)}</div>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPratOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: scheda avanzamento */}
      <Dialog open={aggOpen} onOpenChange={setAggOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuova scheda di avanzamento cantiere</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo *</Label><Input data-testid="agg-titolo" value={agg.titolo} onChange={(e) => setAgg({ ...agg, titolo: e.target.value })} placeholder="Es. Avanzamento SAL 1" /></div>
            <div><Label>Testo aggiornamento *</Label><Textarea rows={4} data-testid="agg-testo" value={agg.testo} onChange={(e) => setAgg({ ...agg, testo: e.target.value })} /></div>
            <div><Label>Fase collegata (opzionale)</Label>
              <select className="w-full border rounded h-10 px-2 mt-1" value={agg.fase_id} onChange={(e) => setAgg({ ...agg, fase_id: e.target.value })}>
                <option value="">— nessuna —</option>
                {(sel.fasi || []).map((f) => <option key={f.id} value={f.id}>{f.nome} ({f.stato})</option>)}
              </select>
            </div>
            <div>
              <Label>Destinatari (soggetti)</Label>
              <div className="border rounded p-2 mt-1 max-h-36 overflow-y-auto space-y-1">
                {(sel.soggetti || []).length === 0 ? <p className="text-xs text-slate-400">Nessun soggetto. Incaricane prima.</p> : sel.soggetti.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm" data-testid={`agg-sog-${s.id}`}><input type="checkbox" checked={agg.soggetti_ids.includes(s.id)} onChange={() => toggleAggSog(s.id)} /> {s.nome} <span className="text-xs text-slate-400">· {s.ruolo}{s.email ? "" : " (no email)"}</span></label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium" data-testid="agg-condomini"><input type="checkbox" checked={agg.includi_condomini} onChange={(e) => setAgg({ ...agg, includi_condomini: e.target.checked })} /> Invia anche a tutti i condòmini degli stabili collegati</label>
            {(sel.documenti || []).filter((d) => d.presente).length > 0 && (
              <div>
                <Label>Allega documenti (opzionale)</Label>
                <div className="border rounded p-2 mt-1 max-h-32 overflow-y-auto space-y-1">
                  {sel.documenti.filter((d) => d.presente).map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={agg.doc_ids.includes(d.id)} onChange={() => toggleAggDoc(d.id)} /> {d.tipologia}</label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAggOpen(false)}>Annulla</Button><Button className="bg-brand" data-testid="agg-send" onClick={sendAgg}><Send className="w-4 h-4 mr-1" /> Invia scheda</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: costo extra */}
      <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{extraEditId ? "Modifica costo extra capitolato" : "Nuovo costo extra capitolato"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Descrizione *</Label><Input data-testid="extra-descrizione" value={extra.descrizione} onChange={(e) => setExtra({ ...extra, descrizione: e.target.value })} placeholder="Es. Rinforzo strutturale imprevisto" /></div>
            <div><Label>Importo (€)</Label><Input type="number" data-testid="extra-importo" value={extra.importo} onChange={(e) => setExtra({ ...extra, importo: e.target.value })} /></div>
            <div><Label>Motivo / note</Label><Textarea rows={2} value={extra.motivo} onChange={(e) => setExtra({ ...extra, motivo: e.target.value })} /></div>
            <p className="text-xs text-slate-400">Il costo nasce come «Proposto»; da approvare/respingere (es. da DL/asseveratore).</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setExtraOpen(false)}>Annulla</Button><Button className="bg-brand" data-testid="extra-save" onClick={saveExtra}>{extraEditId ? "Salva" : "Aggiungi"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: soggetto */}
      <Dialog open={sogOpen} onOpenChange={setSogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Incarica un soggetto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ruolo / carica</Label>
              <select className="w-full border rounded h-10 px-2 mt-1" data-testid="sog-ruolo" value={sog.ruolo} onChange={(e) => setSog({ ...sog, ruolo: e.target.value })}>
                {meta.ruoli.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div><Label>Tipo soggetto</Label>
              <select className="w-full border rounded h-10 px-2 mt-1" data-testid="sog-tipo" value={sog.tipo} onChange={(e) => setSog({ ...sog, tipo: e.target.value, ref_id: "" })}>
                <option value="fornitore">Fornitore (con accesso app)</option>
                <option value="condomino">Condòmino / Consigliere (con accesso app)</option>
                <option value="committente-condominio">Committente – Condominio</option>
                <option value="esterno">Soggetto esterno (Comune/ATS, senza accesso)</option>
              </select>
            </div>
            {sog.tipo === "committente-condominio" && (
              <div className="grid grid-cols-1 gap-2">
                <div><Label>Condominio committente</Label>
                  <select className="w-full border rounded h-10 px-2 mt-1" data-testid="sog-committente" value={sog.ref_id} onChange={(e) => setSog({ ...sog, ref_id: e.target.value })}>
                    <option value="">— seleziona —</option>
                    {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <Input placeholder="Email / PEC di riferimento (opzionale)" data-testid="sog-committente-email" value={sog.email} onChange={(e) => setSog({ ...sog, email: e.target.value })} />
              </div>
            )}
            {sog.tipo === "fornitore" && (
              <div><Label>Fornitore</Label>
                <select className="w-full border rounded h-10 px-2 mt-1" data-testid="sog-fornitore" value={sog.ref_id} onChange={(e) => setSog({ ...sog, ref_id: e.target.value })}>
                  <option value="">— seleziona —</option>
                  {fornitori.map((f) => <option key={f.id} value={f.id}>{f.ragione_sociale}</option>)}
                </select>
              </div>
            )}
            {sog.tipo === "condomino" && (() => {
              // Iter122k11 · Filtra il dropdown ai soli condòmini dei condomìni dell'appalto.
              const appaltoCondIds = (sel?.condominio_ids && sel.condominio_ids.length
                ? sel.condominio_ids
                : (sel?.condominio_id ? [sel.condominio_id] : []));
              const listaFiltrata = appaltoCondIds.length
                ? residenti.filter((r) => appaltoCondIds.includes(r.condominio_id))
                : residenti;
              const nomiCond = (sel?.condominio_nomi || []).join(", ") || sel?.condominio_nome || "questo condominio";
              return (
                <div><Label>Condòmino</Label>
                  <select className="w-full border rounded h-10 px-2 mt-1" data-testid="sog-condomino" value={sog.ref_id} onChange={(e) => setSog({ ...sog, ref_id: e.target.value })}>
                    <option value="">— seleziona —</option>
                    {listaFiltrata.map((r) => <option key={r.id} value={r.id}>{r.nome}{r.condominio ? ` (${r.condominio})` : ""}</option>)}
                  </select>
                  {appaltoCondIds.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1" data-testid="sog-condomino-hint">
                      Mostro solo i condòmini di <b>{nomiCond}</b> ({listaFiltrata.length}).
                    </p>
                  )}
                </div>
              );
            })()}
            {sog.tipo === "esterno" && (
              <div className="grid grid-cols-1 gap-2">
                <Input placeholder="Nome ente/soggetto (es. Comune - Ufficio Tecnico)" data-testid="sog-nome" value={sog.nome} onChange={(e) => setSog({ ...sog, nome: e.target.value })} />
                <Input placeholder="Email / PEC" data-testid="sog-email" value={sog.email} onChange={(e) => setSog({ ...sog, email: e.target.value })} />
                <Input placeholder="Telefono (opzionale)" value={sog.telefono} onChange={(e) => setSog({ ...sog, telefono: e.target.value })} />
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSogOpen(false)}>Annulla</Button><Button className="bg-brand" data-testid="sog-save" onClick={addSoggetto}>Incarica</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: fase */}
      <Dialog open={faseOpen} onOpenChange={setFaseOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{faseEditId ? "Modifica fase / SAL" : "Aggiungi fase / SAL"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome fase</Label><Input data-testid="fase-nome" value={fase.nome} onChange={(e) => setFase({ ...fase, nome: e.target.value })} placeholder="Es. SAL 2" /></div>
            <div>
              <Label>Tipo</Label>
              <select className="w-full border rounded h-10 px-2 mt-1" value={fase.tipo} onChange={(e) => setFase({ ...fase, tipo: e.target.value })}>
                <option value="avvio">Avvio</option><option value="sal">SAL</option><option value="fine">Fine lavori</option><option value="collaudo">Collaudo</option><option value="custom">Altro</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data inizio</Label><div className="mt-1"><DatePickerField testid="fase-data-inizio" value={fase.data_inizio} onChange={(v) => setFase({ ...fase, data_inizio: v })} className="h-10 text-sm" short /></div></div>
              <div><Label>Data fine</Label><div className="mt-1"><DatePickerField testid="fase-data-fine" value={fase.data_fine} onChange={(v) => setFase({ ...fase, data_fine: v })} className="h-10 text-sm" short /></div></div>
            </div>
            {/* Iter122n · Fase 1 · importo disponibile per tutti i tipi fase */}
            <div><Label>Importo (€) {fase.tipo === "sal" ? "SAL" : "previsto per la fase"}</Label>
              <Input type="number" data-testid="fase-importo" value={fase.importo} onChange={(e) => setFase({ ...fase, importo: e.target.value })} />
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={fase.note} onChange={(e) => setFase({ ...fase, note: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setFaseOpen(false)}>Annulla</Button><Button className="bg-brand" data-testid="fase-save" onClick={saveFase}>{faseEditId ? "Salva" : "Aggiungi"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter122o · Fase 2 · Dialog: pagamento (fattura/nota_credito/acconto/saldo) */}
      <Dialog open={pagOpen} onOpenChange={setPagOpen}>
        <DialogContent className="max-w-lg" data-testid="pag-dialog">
          <DialogHeader><DialogTitle>{pagEditId ? "Modifica movimento" : "Nuovo movimento contabile"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["fattura", "nota_credito", "acconto", "saldo"].map((t) => (
                  <button key={t} type="button" data-testid={`pag-tipo-${t}`} onClick={() => setPag({ ...pag, tipo: t })}
                    className={`h-11 rounded-lg border-2 font-bold text-sm flex items-center justify-center gap-1 transition-colors ${pag.tipo === t ? PAG_ICON_CLS[t] : "border-slate-300 bg-white text-slate-500 hover:border-slate-500"}`}>
                    {PAG_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>N. documento</Label><Input data-testid="pag-num" value={pag.numero_documento} onChange={(e) => setPag({ ...pag, numero_documento: e.target.value })} placeholder="Es. F/2026/12" /></div>
              <div><Label>Data</Label><div className="mt-1"><DatePickerField testid="pag-data" value={pag.data} onChange={(v) => setPag({ ...pag, data: v })} className="h-10 text-sm" short /></div></div>
            </div>
            <div><Label>Importo (€) *</Label><Input type="number" step="0.01" data-testid="pag-importo" value={pag.importo} onChange={(e) => setPag({ ...pag, importo: e.target.value })} /></div>
            <div><Label>Note</Label><Textarea rows={2} value={pag.note} onChange={(e) => setPag({ ...pag, note: e.target.value })} placeholder="Es. Acconto 30% SAL 1 · Bonifico BPM" /></div>
            <p className="text-xs text-slate-500">Dopo il salvataggio potrai caricare gli allegati (fattura PDF, copia bonifico) dall&apos;icona <Upload className="w-3 h-3 inline" /> accanto al movimento.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagOpen(false)}>Annulla</Button>
            <Button className="bg-brand" data-testid="pag-save" onClick={savePag}>{pagEditId ? "Salva" : "Aggiungi"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter122p · Fase 3 · Dialog: movimento contabile su extra */}
      <Dialog open={extraPagOpen} onOpenChange={setExtraPagOpen}>
        <DialogContent className="max-w-lg" data-testid="extra-pag-dialog">
          <DialogHeader><DialogTitle>Nuovo movimento su extra capitolato</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["fattura", "nota_credito", "acconto", "saldo"].map((t) => (
                  <button key={t} type="button" data-testid={`extra-pag-tipo-${t}`} onClick={() => setExtraPag({ ...extraPag, tipo: t })}
                    className={`h-11 rounded-lg border-2 font-bold text-sm flex items-center justify-center gap-1 transition-colors ${extraPag.tipo === t ? PAG_ICON_CLS[t] : "border-slate-300 bg-white text-slate-500 hover:border-slate-500"}`}>
                    {PAG_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>N. documento</Label><Input data-testid="extra-pag-num" value={extraPag.numero_documento} onChange={(e) => setExtraPag({ ...extraPag, numero_documento: e.target.value })} /></div>
              <div><Label>Data</Label><div className="mt-1"><DatePickerField testid="extra-pag-data" value={extraPag.data} onChange={(v) => setExtraPag({ ...extraPag, data: v })} className="h-10 text-sm" short /></div></div>
            </div>
            <div><Label>Importo (€) *</Label><Input type="number" step="0.01" data-testid="extra-pag-importo" value={extraPag.importo} onChange={(e) => setExtraPag({ ...extraPag, importo: e.target.value })} /></div>
            <div><Label>Note</Label><Textarea rows={2} value={extraPag.note} onChange={(e) => setExtraPag({ ...extraPag, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtraPagOpen(false)}>Annulla</Button>
            <Button className="bg-brand" data-testid="extra-pag-save" onClick={saveExtraPag}>Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: nuovo documento */}
      <Dialog open={docOpen} onOpenChange={setDocOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Aggiungi documento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Categoria</Label>
              <select className="w-full border rounded h-10 px-2 mt-1" value={newDoc.categoria} onChange={(e) => setNewDoc({ ...newDoc, categoria: e.target.value })}>
                {meta.categorie.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Tipologia documento</Label><Input data-testid="doc-tipologia" list="tipologie-list" value={newDoc.tipologia} onChange={(e) => setNewDoc({ ...newDoc, tipologia: e.target.value })} placeholder="Es. DURC, PIMUS, SAL, Computo metrico…" />
              <datalist id="tipologie-list">{(meta.catalogo || []).map((v, i) => <option key={i} value={v.tipologia} />)}</datalist>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newDoc.obbligatorio} onChange={(e) => setNewDoc({ ...newDoc, obbligatorio: e.target.checked })} /> Documento obbligatorio</label>
            <div><Label>Collega a un soggetto (opzionale)</Label>
              <select className="w-full border rounded h-10 px-2 mt-1" data-testid="doc-soggetto" value={newDoc.soggetto_id} onChange={(e) => setNewDoc({ ...newDoc, soggetto_id: e.target.value })}>
                <option value="">— nessuno —</option>
                {(sel.soggetti || []).map((s) => <option key={s.id} value={s.id}>{s.nome} · {s.ruolo}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">Usa la categoria «Comunicazioni» per acquisire e categorizzare mail/foto/PDF collegandoli a un soggetto.</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDocOpen(false)}>Annulla</Button><Button className="bg-brand" data-testid="doc-save" onClick={addDoc}>Aggiungi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, v }) => (<div className="flex gap-2"><span className="font-semibold text-slate-500 w-32 shrink-0">{k}</span><span className="text-slate-800">{v}</span></div>);

const ProgressBar = ({ label, value, total }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs text-slate-500 mb-1"><span>{label}</span><span>{value}/{total}</span></div>
      <div className="h-2 bg-slate-100 rounded"><div className="h-2 bg-brand rounded" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

function NewAppaltoDialog({ newOpen, setNewOpen, form, setForm, condomini, meta, createAppalto, toggleCond }) {
  return (
    <Dialog open={newOpen} onOpenChange={setNewOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nuovo appalto / commessa</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titolo *</Label><Input data-testid="appalto-titolo" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="Es. Rifacimento facciata" /></div>
          <div><Label>Descrizione</Label><Textarea rows={2} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Importo previsto (€)</Label><Input type="number" data-testid="appalto-importo" value={form.importo_previsto} onChange={(e) => setForm({ ...form, importo_previsto: e.target.value })} /></div>
            <div><Label>Avvio presunto</Label><div className="mt-1"><DatePickerField testid="appalto-avvio" value={form.data_avvio_presunta} onChange={(v) => setForm({ ...form, data_avvio_presunta: v })} className="h-10 text-sm" short /></div></div>
          </div>
          <div><Label>Stato</Label>
            <select className="w-full border rounded h-10 px-2 mt-1" value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })}>
              {meta.stati.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label>Stabili collegati</Label>
            <div className="max-h-40 overflow-y-auto border rounded p-2 mt-1 space-y-1">
              {condomini.length === 0 ? <p className="text-xs text-slate-400">Nessuno stabile</p> : condomini.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.condominio_ids.includes(c.id)} onChange={() => toggleCond(c.id)} /> {c.nome}</label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setNewOpen(false)}>Annulla</Button><Button className="bg-brand" data-testid="appalto-create-save" onClick={createAppalto}>Crea appalto</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Iter123k199 · Sub-tab "Sopralluoghi CoPilot" dentro AppaltoDetail
function CoPilotTab({ sel, canEdit }) {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const load = () => {
    setLoading(true);
    api.get(`/copilot/sessioni?appalto_id=${sel.id}`)
      .then((r) => setList(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sel.id]);

  const startSessione = async () => {
    setStarting(true);
    try {
      const r = await api.post("/copilot/sessioni", { appalto_id: sel.id });
      navigate(`/copilot/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore");
    }
    setStarting(false);
  };

  return (
    <div data-testid="tab-copilot">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm text-slate-500">
          <Sparkles className="w-4 h-4 inline-block mr-1 text-brand" />
          Avvia un sopralluogo sul campo: il cronometro gira, aggiungi note vocali/testuali, foto e chatti col CoPilot.
          A fine sessione: report PDF con costo orario automatico.
        </p>
        {canEdit && (
          <Button data-testid="copilot-start-btn" onClick={startSessione}
            disabled={starting} className="bg-brand hover:bg-[#63122F]">
            <Sparkles className="w-4 h-4 mr-1" /> {starting ? "Avvio…" : "Nuovo sopralluogo CoPilot"}
          </Button>
        )}
      </div>
      {loading ? <p className="text-sm text-slate-400">Caricamento…</p>
        : list.length === 0 ? <p className="text-sm text-slate-400">Nessun sopralluogo ancora registrato.</p>
          : (
            <div className="grid grid-cols-1 gap-2">
              {list.map((s) => (
                <Card key={s.id} className="p-3 border-2 cursor-pointer hover:border-brand"
                  onClick={() => navigate(`/copilot/${s.id}`)} data-testid={`copilot-row-${s.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 text-sm">
                        {fmtDate(s.started_at)} · {s.operatore_nome || "—"}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                        <span><Clock className="w-3 h-3 inline-block mr-0.5" />{s.durata_min || 0}min</span>
                        <span>€ {Number(s.costo_totale || 0).toFixed(2)}</span>
                        <span>{(s.note || []).length} note · {(s.foto || []).length} foto</span>
                        <span className={`px-1.5 rounded text-[10px] font-bold ${s.stato === "chiusa" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {s.stato === "chiusa" ? "CHIUSO" : "IN CORSO"}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
    </div>
  );
}

